import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import shift components (lazy-loaded for performance)
const ShiftsList = React.lazy(() => import("./ShiftsList"));
const NewShift = React.lazy(() => import("./NewShift"));
const ShiftDetails = React.lazy(() => import("./ShiftDetails"));
const RosterDashboard = React.lazy(() => import("./RosterDashboard"));
const MyShifts = React.lazy(() => import("./MyShifts"));
const OpenShifts = React.lazy(() => import("./OpenShifts"));
const EmployeeHours = React.lazy(() => import("./EmployeeHours"));

const shiftsModuleConfig: ModuleConfig = {
    // Module Identity
    id: "shifts",
    name: "Shift Management",
    description: "Schedule and manage employee work shifts with assignments and check-ins",
    version: "1.0.0",
    enabled: true,
    category: "operations",
    order: 5,
    icon: "⏰",
    color: "#805AD5",

    // Routes
    routes: [
        {
            path: "/shifts",
            component: ShiftsList,
            permissions: ["ADMIN"]
        },
        {
            path: "/shifts/roster",
            component: RosterDashboard,
            permissions: ["ADMIN"]
        },
        {
            path: "/shifts/my-shifts",
            component: MyShifts,
            permissions: ["USER", "ADMIN", "EMPLOYEE"]  // Accessible to employees
        },
        {
            path: "/shifts/available",
            component: OpenShifts,
            permissions: ["USER", "ADMIN", "EMPLOYEE"]  // Staff can see and pick up shifts
        },
        {
            path: "/shifts/employee-hours",
            component: EmployeeHours,
            permissions: ["ADMIN"]  // Admin view for payroll/accounting
        },
        {
            path: "/shifts/new",
            component: NewShift,
            permissions: ["ADMIN"]
        },
        {
            path: "/shifts/:id",
            component: ShiftDetails,
            permissions: ["ADMIN"]
        },
        {
            path: "/shifts/edit/:id",
            component: NewShift, // Reuse NewShift component for editing
            permissions: ["ADMIN"]
        }
    ],

    // Navigation
    navigation: [
        {
            label: "My Shifts",
            path: "/shifts/my-shifts",
            icon: "👤",
            permissions: ["USER", "ADMIN", "EMPLOYEE"]  // Accessible to all logged-in users
        },
        {
            label: "All Shifts",
            path: "/shifts",
            icon: "⏰",
            permissions: ["ADMIN"]
        },
        {
            label: "Roster Dashboard",
            path: "/shifts/roster",
            icon: "📊",
            permissions: ["ADMIN"]
        },
        {
            label: "Employee Hours",
            path: "/shifts/employee-hours",
            icon: "⏱️",
            permissions: ["ADMIN"]
        },
        {
            label: "Create Shift",
            path: "/shifts/new",
            icon: "➕",
            permissions: ["ADMIN"]
        },
        {
            label: "Today's Shifts",
            path: "/shifts?filter=today",
            icon: "📅",
            permissions: ["ADMIN"]
        },
        {
            label: "Open Shifts",
            path: "/shifts?filter=open",
            icon: "👥",
            permissions: ["ADMIN"]
        },
        {
            label: "Pick Up Shifts",
            path: "/shifts/available",
            icon: "🙋",
            permissions: ["USER", "ADMIN", "EMPLOYEE"]  // Staff can pick up shifts
        }
    ],

    // Permissions
    permissions: {
        view: ["ADMIN"],
        create: ["ADMIN"],
        edit: ["ADMIN"],
        delete: ["ADMIN"]
    },

    // Quick Actions
    quickActions: [
        {
            label: "My Shifts",
            path: "/shifts/my-shifts",
            icon: "👤",
            description: "View your assigned shifts"
        },
        {
            label: "Roster Dashboard",
            path: "/shifts/roster",
            icon: "📊",
            description: "Calendar view of all shifts"
        },
        {
            label: "Employee Hours",
            path: "/shifts/employee-hours",
            icon: "⏱️",
            description: "View hours by employee for payroll"
        },
        {
            label: "Create Shift",
            path: "/shifts/new",
            icon: "➕",
            description: "Schedule a new shift"
        },
        {
            label: "Today's Shifts",
            path: "/shifts?filter=today",
            icon: "📅",
            description: "View today's shifts"
        },
        {
            label: "Open Shifts",
            path: "/shifts?filter=open",
            icon: "👥",
            description: "View shifts needing staff"
        },
        {
            label: "Pick Up Shifts",
            path: "/shifts/available",
            icon: "🙋",
            description: "Staff: pick up available shifts"
        }
    ]
};

export default shiftsModuleConfig;
